/**
 * Milestone Service
 *
 * Handles all milestone-related operations for project tracking.
 *
 * @module services/milestoneService
 */

import { supabase } from './supabase';
import { buildDefaultMilestoneRows } from '../constants/milestoneTemplate';

/**
 * Seed the default 6-stage milestone template (#460: Cotización →
 * Planeación → Compras → Ejecución → Evaluación → Cierre) for a
 * newly-created project. Called by `createProject` and
 * `createProjectWithQuotation` right after the project row exists.
 *
 * Best-effort: a failure here does not (and must not) roll back project
 * creation — the project already exists and is usable without milestones,
 * exactly like it was before #460. Callers surface the failure as a
 * non-fatal warning instead of throwing.
 *
 * @param {string} projectId - Project UUID.
 * @returns {Promise<{data, error}>}
 */
export const createDefaultMilestones = async (projectId) => {
  try {
    if (!projectId) throw new Error('projectId is required');

    const { data: { user }, error: userError } = await supabase.auth.getUser();
    if (userError) throw userError;

    const rows = buildDefaultMilestoneRows(projectId, user?.id);

    const { data, error } = await supabase
      .from('milestones')
      .insert(rows)
      .select();

    if (error) throw error;

    return { data, error: null };
  } catch (error) {
    console.error('Create default milestones error:', error);
    return { data: null, error };
  }
};

/**
 * Get milestones for a project
 * @param {string} projectId - Project ID
 * @param {Object} filters - Optional filters
 * @param {string} filters.status - Filter by status
 * @returns {Promise<{data, error}>}
 */
export const getProjectMilestones = async (projectId, filters = {}) => {
  try {
    let query = supabase
      .from('milestones')
      .select(`
        *,
        created_by_profile:profiles!milestones_created_by_fkey(id, full_name)
      `)
      .eq('project_id', projectId)
      .order('order_index', { ascending: true });

    if (filters.status) {
      query = query.eq('status', filters.status);
    }

    const { data, error } = await query;

    if (error) throw error;

    // For each milestone, get task counts
    if (data && data.length > 0) {
      const milestoneIds = data.map(m => m.id);
      const { data: tasks, error: tasksError } = await supabase
        .from('tasks')
        .select('id, milestone_id, status')
        .in('milestone_id', milestoneIds);

      if (!tasksError && tasks) {
        const tasksByMilestone = {};
        tasks.forEach(task => {
          if (!tasksByMilestone[task.milestone_id]) {
            tasksByMilestone[task.milestone_id] = { total: 0, completed: 0 };
          }
          tasksByMilestone[task.milestone_id].total++;
          if (task.status === 'completed') {
            tasksByMilestone[task.milestone_id].completed++;
          }
        });

        data.forEach(milestone => {
          const counts = tasksByMilestone[milestone.id] || { total: 0, completed: 0 };
          milestone.taskCount = counts.total;
          milestone.completedTaskCount = counts.completed;
          milestone.taskProgress = counts.total > 0
            ? Math.round((counts.completed / counts.total) * 100)
            : 0;
        });
      }
    }

    return { data, error: null };
  } catch (error) {
    console.error('Get project milestones error:', error);
    return { data: null, error };
  }
};

/**
 * Get a single milestone by ID
 * @param {string} milestoneId - Milestone ID
 * @returns {Promise<{data, error}>}
 */
export const getMilestone = async (milestoneId) => {
  try {
    const { data, error } = await supabase
      .from('milestones')
      .select(`
        *,
        created_by_profile:profiles!milestones_created_by_fkey(id, full_name)
      `)
      .eq('id', milestoneId)
      .single();

    if (error) throw error;

    return { data, error: null };
  } catch (error) {
    console.error('Get milestone error:', error);
    return { data: null, error };
  }
};

/**
 * Create a new milestone
 * @param {Object} milestoneData - Milestone data
 * @returns {Promise<{data, error}>}
 */
export const createMilestone = async (milestoneData) => {
  try {
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    if (userError) throw userError;

    const { data, error } = await supabase
      .from('milestones')
      .insert({
        ...milestoneData,
        created_by: user.id,
      })
      .select()
      .single();

    if (error) throw error;

    return { data, error: null };
  } catch (error) {
    console.error('Create milestone error:', error);
    return { data: null, error };
  }
};

/**
 * Update an existing milestone
 * @param {string} milestoneId - Milestone ID
 * @param {Object} updates - Fields to update
 * @returns {Promise<{data, error}>}
 */
export const updateMilestone = async (milestoneId, updates) => {
  try {
    const { data, error } = await supabase
      .from('milestones')
      .update(updates)
      .eq('id', milestoneId)
      .select()
      .single();

    if (error) throw error;

    return { data, error: null };
  } catch (error) {
    console.error('Update milestone error:', error);
    return { data: null, error };
  }
};

/**
 * Complete a milestone
 * @param {string} milestoneId - Milestone ID
 * @param {string} completedDate - Optional completion date (defaults to now)
 * @returns {Promise<{data, error}>}
 */
export const completeMilestone = async (milestoneId, completedDate = null) => {
  try {
    // NOTE: the live `milestones` table has no `completed_date` column (#284).
    // Real columns are `completed_at` (timestamptz) and `is_completed` (boolean).
    const { data, error } = await supabase
      .from('milestones')
      .update({
        status: 'completed',
        is_completed: true,
        completed_at: completedDate ? new Date(completedDate).toISOString() : new Date().toISOString(),
      })
      .eq('id', milestoneId)
      .select()
      .single();

    if (error) throw error;

    return { data, error: null };
  } catch (error) {
    console.error('Complete milestone error:', error);
    return { data: null, error };
  }
};

/**
 * Delete a milestone
 * @param {string} milestoneId - Milestone ID
 * @returns {Promise<{success, error}>}
 */
export const deleteMilestone = async (milestoneId) => {
  try {
    const { error } = await supabase
      .from('milestones')
      .delete()
      .eq('id', milestoneId);

    if (error) throw error;

    return { success: true, error: null };
  } catch (error) {
    console.error('Delete milestone error:', error);
    return { success: false, error };
  }
};
